import React, { useEffect, useState } from "react";

import { FaTimes, FaImage, FaVideo, FaUpload, FaTrash } from "react-icons/fa";

import { addDoc, collection, doc, updateDoc } from "firebase/firestore";

import { db } from "../../../firebase/firebase";

import "./ProjectModal.css";

// ============================================================
// EMPTY PROJECT
// ============================================================

const EMPTY_PROJECT = {
  title: "",
  slug: "",
  sport: "",
  surface: "",
  client: "",
  location: "",
  state: "",
  completionDate: "",
  courtLength: "",
  courtWidth: "",
  area: "",
  shortDescription: "",
  description: "",
  featured: false,
  seoTitle: "",
  seoDescription: "",

  coverImage: "",
  galleryImages: [],
  video: "",
};

// ============================================================
// CLOUDINARY UPLOAD
// ============================================================

const uploadToCloudinary = async (file) => {
  if (!file) {
    return "";
  }

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  if (!cloudName) {
    throw new Error("Cloudinary Cloud Name is missing. Check your .env file.");
  }

  if (!uploadPreset) {
    throw new Error(
      "Cloudinary Upload Preset is missing. Check your .env file.",
    );
  }

  const formData = new FormData();

  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);

  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`;

  const response = await fetch(uploadUrl, {
    method: "POST",
    body: formData,
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.error?.message || "Cloudinary upload failed.");
  }

  if (!data?.secure_url) {
    throw new Error("Cloudinary did not return a file URL.");
  }

  return data.secure_url;
};

// ============================================================
// DELETE CLOUDINARY FILE
// ============================================================
//
// IMPORTANT:
// This calls YOUR secure backend.
// Cloudinary API secret stays on the server.
//
// ============================================================

const deleteFromCloudinary = async (url) => {
  if (!url) {
    return;
  }

  try {
    const response = await fetch("/api/cloudinary/delete", {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({
        url,
      }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || "Cloudinary deletion failed.");
    }

    console.log("Cloudinary file deleted:", url);
  } catch (error) {
    console.error("Cloudinary delete error:", error);

    throw error;
  }
};

// ============================================================
// PROJECT MODAL
// ============================================================

const ProjectModal = ({ closeModal, projectToEdit }) => {
  const isEditMode = Boolean(projectToEdit?.id);

  // ==========================================================
  // PROJECT
  // ==========================================================

  const [project, setProject] = useState(EMPTY_PROJECT);

  // ==========================================================
  // COVER
  // ==========================================================

  const [coverImageFile, setCoverImageFile] = useState(null);

  const [coverPreview, setCoverPreview] = useState("");

  const [coverRemoved, setCoverRemoved] = useState(false);

  // ==========================================================
  // GALLERY
  // ==========================================================

  const [galleryItems, setGalleryItems] = useState([]);

  // ==========================================================
  // VIDEO
  // ==========================================================

  const [videoFile, setVideoFile] = useState(null);

  const [videoPreview, setVideoPreview] = useState("");

  const [videoRemoved, setVideoRemoved] = useState(false);

  // ==========================================================
  // SAVING
  // ==========================================================

  const [saving, setSaving] = useState(false);

  // ==========================================================
  // LOAD PROJECT
  // ==========================================================

  useEffect(() => {
    if (!projectToEdit) {
      // ADD MODE

      setProject({
        ...EMPTY_PROJECT,
        galleryImages: [],
      });

      setCoverImageFile(null);
      setCoverPreview("");
      setCoverRemoved(false);

      setGalleryItems([]);

      setVideoFile(null);
      setVideoPreview("");
      setVideoRemoved(false);

      return;
    }

    // EDIT MODE

    setProject({
      ...EMPTY_PROJECT,
      ...projectToEdit,

      galleryImages: Array.isArray(projectToEdit.galleryImages)
        ? projectToEdit.galleryImages
        : [],
    });

    // COVER

    setCoverImageFile(null);

    setCoverPreview(projectToEdit.coverImage || "");

    setCoverRemoved(false);

    // GALLERY

    const existingGallery = Array.isArray(projectToEdit.galleryImages)
      ? projectToEdit.galleryImages
      : [];

    setGalleryItems(
      existingGallery
        .filter((image) => typeof image === "string" && image.trim() !== "")
        .map((image) => ({
          url: image,
          file: null,
          isNew: false,
        })),
    );

    // VIDEO

    setVideoFile(null);

    setVideoPreview(projectToEdit.video || "");

    setVideoRemoved(false);
  }, [projectToEdit]);

  // ==========================================================
  // FORM CHANGE
  // ==========================================================

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setProject((previous) => ({
      ...previous,

      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ==========================================================
  // COVER IMAGE
  // ==========================================================

  const handleCoverImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setCoverImageFile(file);

    setCoverRemoved(false);

    const previewUrl = URL.createObjectURL(file);

    setCoverPreview(previewUrl);

    event.target.value = "";
  };

  // ==========================================================
  // REMOVE COVER IMAGE
  // ==========================================================

  const removeCoverImage = () => {
    if (!coverPreview) {
      return;
    }

    setCoverPreview("");

    setCoverImageFile(null);

    setCoverRemoved(true);

    setProject((previous) => ({
      ...previous,
      coverImage: "",
    }));
  };

  // ==========================================================
  // GALLERY IMAGE SELECT
  // ==========================================================

  const handleGalleryChange = (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    const newItems = files.map((file) => ({
      url: URL.createObjectURL(file),

      file,

      isNew: true,
    }));

    setGalleryItems((previous) => [...previous, ...newItems]);

    event.target.value = "";
  };

  // ==========================================================
  // REMOVE GALLERY IMAGE
  // ==========================================================

  const removeGalleryImage = (index) => {
    setGalleryItems((previous) =>
      previous.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  // ==========================================================
  // VIDEO SELECT
  // ==========================================================

  const handleVideoChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setVideoFile(file);

    setVideoRemoved(false);

    const previewUrl = URL.createObjectURL(file);

    setVideoPreview(previewUrl);

    event.target.value = "";
  };

  // ==========================================================
  // REMOVE VIDEO
  // ==========================================================

  const removeVideo = () => {
    if (!videoPreview) {
      return;
    }

    setVideoPreview("");

    setVideoFile(null);

    setVideoRemoved(true);

    setProject((previous) => ({
      ...previous,
      video: "",
    }));
  };

  // ==========================================================
  // SAVE PROJECT
  // ==========================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) {
      return;
    }

    try {
      setSaving(true);

      // ====================================================
      // VALIDATION
      // ====================================================

      if (!project.title.trim()) {
        throw new Error("Project title is required.");
      }

      if (!project.slug.trim()) {
        throw new Error("Project slug is required.");
      }

      if (!project.sport) {
        throw new Error("Please select a sport.");
      }

      if (!project.surface) {
        throw new Error("Please select a surface.");
      }

      // ====================================================
      // OLD MEDIA
      // ====================================================

      const oldCoverImage = projectToEdit?.coverImage || "";

      const oldVideo = projectToEdit?.video || "";

      const oldGalleryImages = Array.isArray(projectToEdit?.galleryImages)
        ? projectToEdit.galleryImages
        : [];

      // ====================================================
      // COVER IMAGE
      // ====================================================

      let finalCoverImage = "";

      // User removed cover
      if (coverRemoved) {
        finalCoverImage = "";
      }

      // New cover selected
      else if (coverImageFile) {
        console.log("Uploading new cover image...");

        finalCoverImage = await uploadToCloudinary(coverImageFile);

        console.log("New cover:", finalCoverImage);
      }

      // Existing cover
      else {
        finalCoverImage = project.coverImage || oldCoverImage || "";
      }

      // ====================================================
      // GALLERY
      // ====================================================

      const finalGalleryImages = [];

      for (const item of galleryItems) {
        // Existing image
        if (!item.isNew && item.url) {
          finalGalleryImages.push(item.url);

          continue;
        }

        // New image
        if (item.isNew && item.file) {
          console.log("Uploading gallery image...");

          const uploadedUrl = await uploadToCloudinary(item.file);

          finalGalleryImages.push(uploadedUrl);
        }
      }

      // ====================================================
      // VIDEO
      // ====================================================

      let finalVideo = "";

      // User removed video
      if (videoRemoved) {
        finalVideo = "";
      }

      // New video
      else if (videoFile) {
        console.log("Uploading new video...");

        finalVideo = await uploadToCloudinary(videoFile);

        console.log("New video:", finalVideo);
      }

      // Existing video
      else {
        finalVideo = project.video || oldVideo || "";
      }

      // ====================================================
      // FIREBASE DATA
      // ====================================================

      const projectData = {
        title: project.title.trim(),

        slug: project.slug.trim(),

        sport: project.sport,

        surface: project.surface,

        client: project.client || "",

        location: project.location || "",

        state: project.state || "",

        completionDate: project.completionDate || "",

        courtLength: project.courtLength || "",

        courtWidth: project.courtWidth || "",

        area: project.area || "",

        shortDescription: project.shortDescription || "",

        description: project.description || "",

        featured: Boolean(project.featured),

        seoTitle: project.seoTitle || "",

        seoDescription: project.seoDescription || "",

        coverImage: finalCoverImage,

        galleryImages: finalGalleryImages,

        video: finalVideo,

        updatedAt: new Date().toISOString(),
      };

      // ====================================================
      // ADD
      // ====================================================

      if (!isEditMode) {
        projectData.createdAt = new Date().toISOString();

        await addDoc(collection(db, "projects"), projectData);

        alert("Project added successfully!");
      }

      // ====================================================
      // UPDATE
      // ====================================================
      else {
        await updateDoc(doc(db, "projects", projectToEdit.id), projectData);

        // ==================================================
        // DELETE OLD COVER FROM CLOUDINARY
        // ==================================================

        const coverWasChanged =
          coverImageFile && oldCoverImage && finalCoverImage !== oldCoverImage;

        const coverWasRemoved = coverRemoved && oldCoverImage;

        if (coverWasChanged || coverWasRemoved) {
          try {
            await deleteFromCloudinary(oldCoverImage);
          } catch (deleteError) {
            console.error("Old cover deletion failed:", deleteError);
          }
        }

        // ==================================================
        // DELETE OLD VIDEO
        // ==================================================

        const videoWasChanged =
          videoFile && oldVideo && finalVideo !== oldVideo;

        const videoWasRemoved = videoRemoved && oldVideo;

        if (videoWasChanged || videoWasRemoved) {
          try {
            await deleteFromCloudinary(oldVideo);
          } catch (deleteError) {
            console.error("Old video deletion failed:", deleteError);
          }
        }

        // ==================================================
        // DELETE REMOVED GALLERY IMAGES
        // ==================================================

        const newGallerySet = new Set(finalGalleryImages);

        const removedGalleryImages = oldGalleryImages.filter(
          (oldImage) => !newGallerySet.has(oldImage),
        );

        for (const oldImage of removedGalleryImages) {
          try {
            await deleteFromCloudinary(oldImage);
          } catch (deleteError) {
            console.error("Gallery deletion failed:", deleteError);
          }
        }

        alert("Project updated successfully!");
      }

      // ====================================================
      // CLOSE
      // ====================================================

      closeModal();
    } catch (error) {
      console.error("PROJECT SAVE ERROR:", error);

      alert(error?.message || "Failed to save project.");
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="project-modal-overlay">
      <div className="project-modal">
        {/* ==================================================
            HEADER
        ================================================== */}

        <div className="project-modal-header">
          <div>
            <h2>{isEditMode ? "Edit Project" : "Add New Project"}</h2>

            <p>
              {isEditMode
                ? "Update your completed sports construction project."
                : "Add a completed sports construction project."}
            </p>
          </div>

          <button
            type="button"
            className="modal-close"
            onClick={closeModal}
            disabled={saving}
          >
            <FaTimes />
          </button>
        </div>

        {/* ==================================================
            FORM
        ================================================== */}

        <form onSubmit={handleSubmit}>
          {/* ==================================================
              PROJECT INFORMATION
          ================================================== */}

          <div className="modal-section">
            <div className="section-title">
              <h3>Project Information</h3>

              <span>Basic project details</span>
            </div>

            <div className="modal-form-grid">
              <div className="modal-form-group full-width">
                <label>Project Title *</label>

                <input
                  type="text"
                  name="title"
                  value={project.title}
                  onChange={handleChange}
                  placeholder="Example: Premium Basketball Court"
                  required
                />
              </div>

              <div className="modal-form-group">
                <label>Slug *</label>

                <input
                  type="text"
                  name="slug"
                  value={project.slug}
                  onChange={handleChange}
                  placeholder="premium-basketball-court-delhi"
                  required
                />
              </div>

              <div className="modal-form-group">
                <label>Sport *</label>

                <select
                  name="sport"
                  value={project.sport}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Sport</option>

                  <option value="Basketball">Basketball</option>

                  <option value="Tennis">Tennis</option>

                  <option value="Badminton">Badminton</option>

                  <option value="Football">Football</option>

                  <option value="Volleyball">Volleyball</option>

                  <option value="Running Track">Running Track</option>

                  <option value="Pickleball">Pickleball</option>

                  <option value="Squash">Squash</option>

                  <option value="Cricket">Cricket</option>
                </select>
              </div>

              <div className="modal-form-group">
                <label>Surface *</label>

                <select
                  name="surface"
                  value={project.surface}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Surface</option>

                  <option value="Acrylic">Acrylic</option>

                  <option value="PU">PU</option>

                  <option value="Synthetic Turf">Synthetic Turf</option>

                  <option value="EPDM">EPDM</option>

                  <option value="Wooden">Wooden</option>
                </select>
              </div>

              <div className="modal-form-group">
                <label>Client Name</label>

                <input
                  type="text"
                  name="client"
                  value={project.client}
                  onChange={handleChange}
                  placeholder="Client / Organization"
                />
              </div>

              <div className="modal-form-group">
                <label>City / Location</label>

                <input
                  type="text"
                  name="location"
                  value={project.location}
                  onChange={handleChange}
                  placeholder="New Delhi"
                />
              </div>

              <div className="modal-form-group">
                <label>State</label>

                <input
                  type="text"
                  name="state"
                  value={project.state}
                  onChange={handleChange}
                  placeholder="Delhi"
                />
              </div>

              <div className="modal-form-group">
                <label>Completion Date</label>

                <input
                  type="date"
                  name="completionDate"
                  value={project.completionDate}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* ==================================================
              DIMENSIONS
          ================================================== */}

          <div className="modal-section">
            <div className="section-title">
              <h3>Project Dimensions</h3>

              <span>Optional measurements</span>
            </div>

            <div className="modal-form-grid">
              <div className="modal-form-group">
                <label>Length (ft)</label>

                <input
                  type="number"
                  name="courtLength"
                  value={project.courtLength}
                  onChange={handleChange}
                  placeholder="94"
                />
              </div>

              <div className="modal-form-group">
                <label>Width (ft)</label>

                <input
                  type="number"
                  name="courtWidth"
                  value={project.courtWidth}
                  onChange={handleChange}
                  placeholder="50"
                />
              </div>

              <div className="modal-form-group">
                <label>Total Area (sq.ft)</label>

                <input
                  type="number"
                  name="area"
                  value={project.area}
                  onChange={handleChange}
                  placeholder="4700"
                />
              </div>
            </div>
          </div>

          {/* ==================================================
              DESCRIPTION
          ================================================== */}

          <div className="modal-section">
            <div className="section-title">
              <h3>Project Description</h3>
            </div>

            <div className="modal-form-group">
              <label>Short Description</label>

              <textarea
                name="shortDescription"
                value={project.shortDescription}
                onChange={handleChange}
                rows="3"
                placeholder="Short description shown on project cards..."
              />
            </div>

            <div className="modal-form-group">
              <label>Full Description</label>

              <textarea
                name="description"
                value={project.description}
                onChange={handleChange}
                rows="6"
                placeholder="Describe the project, construction process, materials used, location, client requirements, etc."
              />
            </div>
          </div>

          {/* ==================================================
              MEDIA
          ================================================== */}

          <div className="modal-section">
            <div className="section-title">
              <h3>Project Media</h3>

              <span>Images and video are stored in Cloudinary</span>
            </div>

            {/* =================================================
                COVER IMAGE
            ================================================= */}

            <div className="upload-box">
              <div className="upload-label">
                <FaImage />

                <div>
                  <strong>Cover Image</strong>

                  <small>Main project image</small>
                </div>
              </div>

              <div className="media-actions">
                <label className="upload-button">
                  <FaUpload />

                  {coverPreview ? "Change Cover Image" : "Choose Cover Image"}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    hidden
                  />
                </label>

                {coverPreview && (
                  <button
                    type="button"
                    className="remove-media-button"
                    onClick={removeCoverImage}
                    disabled={saving}
                  >
                    <FaTrash />
                    Remove Cover
                  </button>
                )}
              </div>

              {coverPreview && (
                <div className="cover-preview">
                  <img src={coverPreview} alt="Project cover" />
                </div>
              )}
            </div>

            {/* =================================================
                GALLERY
            ================================================= */}

            <div className="upload-box">
              <div className="upload-label">
                <FaImage />

                <div>
                  <strong>Gallery Images</strong>

                  <small>Add multiple project images</small>
                </div>
              </div>

              <label className="upload-button">
                <FaUpload />
                Add Gallery Images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryChange}
                  hidden
                />
              </label>

              {galleryItems.length > 0 && (
                <div className="gallery-preview">
                  {galleryItems.map((item, index) => (
                    <div
                      className="gallery-preview-item"
                      key={`${item.url}-${index}`}
                    >
                      <img src={item.url} alt={`Gallery ${index + 1}`} />

                      <button
                        type="button"
                        className="gallery-remove"
                        onClick={() => removeGalleryImage(index)}
                        disabled={saving}
                      >
                        <FaTrash />
                      </button>

                      {item.isNew && (
                        <span className="new-media-badge">New</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* =================================================
                VIDEO
            ================================================= */}

            <div className="upload-box">
              <div className="upload-label">
                <FaVideo />

                <div>
                  <strong>Project Video</strong>

                  <small>Optional project video</small>
                </div>
              </div>

              <div className="media-actions">
                <label className="upload-button">
                  <FaUpload />

                  {videoPreview ? "Change Video" : "Choose Video"}

                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                    hidden
                  />
                </label>

                {videoPreview && (
                  <button
                    type="button"
                    className="remove-media-button"
                    onClick={removeVideo}
                    disabled={saving}
                  >
                    <FaTrash />
                    Remove Video
                  </button>
                )}
              </div>

              {videoPreview && (
                <div className="video-preview-wrapper">
                  <video
                    className="project-video-preview"
                    src={videoPreview}
                    controls
                  />
                </div>
              )}
            </div>
          </div>

          {/* ==================================================
              SEO
          ================================================== */}

          <div className="modal-section">
            <div className="section-title">
              <h3>SEO Settings</h3>

              <span>Search engine optimization</span>
            </div>

            <div className="modal-form-group">
              <label>SEO Title</label>

              <input
                type="text"
                name="seoTitle"
                value={project.seoTitle}
                onChange={handleChange}
                placeholder="Best Basketball Court Construction in Delhi"
              />
            </div>

            <div className="modal-form-group">
              <label>SEO Description</label>

              <textarea
                name="seoDescription"
                value={project.seoDescription}
                onChange={handleChange}
                rows="4"
                placeholder="SEO description for this project..."
              />
            </div>
          </div>

          {/* ==================================================
              FEATURED
          ================================================== */}

          <div className="featured-project">
            <input
              type="checkbox"
              name="featured"
              checked={project.featured}
              onChange={handleChange}
            />

            <div>
              <strong>Featured Project</strong>

              <p>
                Display this project in the featured projects section of the
                website.
              </p>
            </div>
          </div>

          {/* ==================================================
              FOOTER
          ================================================== */}

          <div className="project-modal-footer">
            <button
              type="button"
              className="modal-cancel"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </button>

            <button type="submit" className="modal-save" disabled={saving}>
              {saving
                ? "Uploading & Saving..."
                : isEditMode
                  ? "Update Project"
                  : "Save Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectModal;
